/**
 * Tema ve yoğunluk tercihleri (08 §5.5, §11.10). Anahtarlar theme-init.js ile aynıdır.
 *
 * Tarayıcı deposu yalnız ilk boyamayı hızlandırır; tercihin kaynağı sunucudaki kullanıcı kaydıdır (panel
 * `me/ui-preferences`, backoffice `platform/me/ui-preferences`; aynı kayıt):
 *
 * - `ticarishift.themePreference`: tercihin kendisi (Açık, Koyu, Sistem). theme-init.js ilk boyamadan önce bunu çözer.
 * - `ticarishift.theme`: çözülmüş tema (açık/koyu). Tercih yazılmamış eski depoda (S23 öncesi) theme-init.js bunu okur.
 * - `ticarishift.density`: yoğunluk.
 *
 * Depo boşsa sistem teması kullanılır (08 §11.10). "Sistem" seçiliyken işletim sisteminin tercihi değişince tema da
 * hemen değişir.
 */
export type Theme = 'light' | 'dark';
export type Density = 'comfortable' | 'compact';
/** Kullanıcının tema tercihi; sunucunun `UiTheme` değerleriyle aynıdır. */
export type ThemePreference = 'Light' | 'Dark' | 'System';

export const THEME_STORAGE_KEY = 'ticarishift.theme';
export const THEME_PREFERENCE_STORAGE_KEY = 'ticarishift.themePreference';
export const DENSITY_STORAGE_KEY = 'ticarishift.density';

/** Seçicilerin sırası (avatar menüsü, Profil, backoffice hesap menüsü). */
export const THEME_PREFERENCES: readonly ThemePreference[] = ['Light', 'Dark', 'System'];

const DARK_QUERY = '(prefers-color-scheme: dark)';

function remember(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Depo kapalıysa tercih yalnız bu sayfada geçerlidir.
  }
}

function recall(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function currentTheme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

export function currentDensity(): Density {
  return document.documentElement.dataset.density === 'compact' ? 'compact' : 'comfortable';
}

export function applyTheme(theme: Theme, persist = true) {
  document.documentElement.dataset.theme = theme;
  if (persist) {
    remember(THEME_STORAGE_KEY, theme);
  }
}

export function applyDensity(density: Density, persist = true) {
  document.documentElement.dataset.density = density;
  if (persist) {
    remember(DENSITY_STORAGE_KEY, density);
  }
}

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'Light' || value === 'Dark' || value === 'System';
}

/** Depodaki tercih; yoksa ya da depo kapalıysa `null`. */
export function storedThemePreference(): ThemePreference | null {
  const value = recall(THEME_PREFERENCE_STORAGE_KEY);
  return isThemePreference(value) ? value : null;
}

function darkQuery(): MediaQueryList | null {
  return typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null;
}

let systemQuery: MediaQueryList | null = null;

function followSystem() {
  if (systemQuery) {
    applyTheme(systemQuery.matches ? 'dark' : 'light');
  }
}

/** İşletim sisteminin tercihini uygular ve değişikliğini izlemeye başlar (bir kez). */
function trackSystem() {
  const query = systemQuery ?? darkQuery();
  applyTheme(query?.matches ? 'dark' : 'light');
  if (!systemQuery && query) {
    systemQuery = query;
    query.addEventListener('change', followSystem);
  }
}

function untrackSystem() {
  if (systemQuery) {
    systemQuery.removeEventListener('change', followSystem);
    systemQuery = null;
  }
}

/**
 * Tema tercihini uygular ve depoya yazar (tercih ve çözülmüş tema): Açık ve Koyu zorlar, Sistem işletim sisteminin
 * tercihini izler. Sunucudaki tercih (oturum bağlamı) yüklenince ve kullanıcı seçim yapınca çağrılır.
 */
export function applyThemePreference(preference: ThemePreference) {
  remember(THEME_PREFERENCE_STORAGE_KEY, preference);
  if (preference === 'System') {
    trackSystem();
    return;
  }

  untrackSystem();
  applyTheme(preference === 'Dark' ? 'dark' : 'light');
}

/**
 * Açılışta (React çizilmeden önce) depodaki tercihi yeniden kurar: theme-init.js temayı ilk boyamadan önce yazmıştır;
 * burada "Sistem"in işletim sistemi değişikliğini izlemesi başlar. Tercih yazılmamış eski depoda (yalnız çözülmüş tema
 * varsa) theme-init.js'in uyguladığı tema korunur; depo tümüyle boşsa "Sistem" yazılır ve izlenir (08 §11.10). Oturum
 * açılınca sunucudaki tercih uygulanır.
 */
export function restoreThemePreference() {
  const preference = storedThemePreference();
  if (preference) {
    applyThemePreference(preference);
    return;
  }

  const legacy = recall(THEME_STORAGE_KEY);
  if (legacy === 'light' || legacy === 'dark') {
    return;
  }

  applyThemePreference('System');
}
